import type { IPayloadData, OnCompleteCallback, SendPOSTFunction } from "@microsoft/applicationinsights-core-js";
import { API_VERSIONS } from "@/config/api";
import { SMART_DEBRIEF_API } from "@/config/const";
import authenticationConfig from "@/features/configuration";
import { recoverTokenInfo } from "@/utils/recoverTokenInfo";

const TELEMETRY_URL = `${SMART_DEBRIEF_API}/${API_VERSIONS.v0}/telemetry`;

// Custom SDK transport (see docs/application-insights-rum.md): sends telemetry to our own
// backend proxy instead of Azure directly, so the real Application Insights connection string
// never reaches the client. Reads the auth token fresh on every send (not baked in at SDK-init
// time) so telemetry is only ever sent for authenticated sessions, and never goes stale across
// a long-lived session the way a static header would.
export const telemetrySendPost: SendPOSTFunction = (payload: IPayloadData, oncomplete: OnCompleteCallback) => {
  const token = recoverTokenInfo();

  if (!token) {
    // Not logged in — drop the batch rather than send it. Report success so the SDK doesn't
    // treat this as a failure and retry.
    oncomplete(200, {});
    return;
  }

  fetch(TELEMETRY_URL, {
    method: "POST",
    headers: {
      // The SDK's own native sender sets Content-Type directly on its XHR object rather than
      // storing it in payload.headers (confirmed in SenderPostManager.js), so it's never
      // present here — set it ourselves. Body is always a JSON array of telemetry envelopes.
      "Content-Type": "application/json",
      ...payload.headers,
      Authorization: `Bearer ${token}`,
      "X-iBabs-Device-Id": authenticationConfig.authenticationDeviceKey,
    },
    // TS 5.7 made typed arrays generic (Uint8Array<ArrayBufferLike>), which no longer
    // structurally matches DOM's BodyInit (expects Uint8Array<ArrayBuffer>) even though fetch
    // accepts it fine at runtime — known TS/DOM-lib friction, not a real type mismatch.
    body: payload.data as BodyInit,
    keepalive: true,
    // Without this, fetch defaults to same-origin credentials and drops cross-origin cookies
    // (e.g. Cloudflare Access's CF_Authorization) that the app's other requests carry via
    // axios's withCredentials: true — causing this call alone to get 302'd back to SSO login
    // even after completing the Access handshake elsewhere in the app.
    credentials: "include",
  })
    .then(async (response) => {
      const responseText = await response.text();
      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      oncomplete(response.status, headers, responseText, payload);
    })
    .catch(() => {
      // Network failure — report as a server error so the SDK's own retry/backoff kicks in.
      oncomplete(0, {});
    });
};
